import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import RecipeCard from '../components/recipes/RecipeCard';
import { TrendingUp, Clock, Sparkles, ChevronRight, Globe } from 'lucide-react';
import { Card } from '../components/common';
import { DEFAULT_CATEGORIES, CATEGORY_EMOJIS } from '../constants/recipeConstants';

const SectionHeader = ({ icon: Icon, title, onViewAll }) => (
  <div className="flex items-center justify-between mb-3">
    <h3 className="text-base font-semibold text-gray-900 dark:text-night-text inline-flex items-center gap-2">
      <Icon size={18} aria-hidden="true" />
      {title}
    </h3>
    {onViewAll && (
      <button
        onClick={onViewAll}
        className="inline-flex items-center text-sm text-primary-600 dark:text-primary-400 hover:underline"
      >
        View all <ChevronRight size={16} aria-hidden="true" />
      </button>
    )}
  </div>
);

const HorizontalScroll = ({ children }) => (
  <div className="-mx-4 px-4 overflow-x-auto">
    <div className="flex gap-3 pb-2 [&>*]:w-[160px] [&>*]:flex-shrink-0">{children}</div>
  </div>
);

// Derived from the canonical category constants so newly added categories
// (e.g. Family Meals, Baby & Toddler) surface here without page edits.
// Tolerates both constant shapes: { name, position } and { id, label }.
const CATEGORIES = [...DEFAULT_CATEGORIES]
  .sort((a, b) => (a.position ?? 99) - (b.position ?? 99))
  .map((cat) => {
    const name = cat.label || cat.name;
    return {
      key: cat.id || name,
      name,
      emoji: cat.emoji || CATEGORY_EMOJIS[name] || CATEGORY_EMOJIS[cat.name] || '🍴',
    };
  });

export default function ExplorePage() {
  const { recipes, setActiveCategory } = useApp();
  const navigate = useNavigate();

  const heroRecipe = useMemo(() => {
    const top = recipes.filter((r) => (r.communityRating || 0) >= 4.5 && r.images?.length > 0);
    return top[Math.floor(Math.random() * top.length)] || recipes[0];
  }, [recipes]);

  const trendingRecipes = useMemo(
    () => [...recipes]
      .filter((r) => (r.communityRating || 0) > 0)
      .sort((a, b) => (b.communityRating || 0) - (a.communityRating || 0))
      .slice(0, 10),
    [recipes]
  );

  const newRecipes = useMemo(
    () => [...recipes].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)).slice(0, 10),
    [recipes]
  );

  const activeRegions = useMemo(() => {
    const regions = [...new Set(recipes.map((r) => r.region).filter(Boolean))];
    return regions.sort();
  }, [recipes]);

  return (
    <div className="space-y-6">
      {heroRecipe && (
        <div
          onClick={() => navigate(`/recipes/${heroRecipe.id}`)}
          className="relative -mx-4 sm:mx-0 sm:rounded-2xl overflow-hidden h-56 bg-cover bg-center cursor-pointer"
          style={{
            backgroundImage:
              heroRecipe.images?.[0]?.url
                ? `url(${heroRecipe.images[0].url})`
                : heroRecipe.image ? `url(${heroRecipe.image})` : undefined,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
          <div className="relative h-full flex flex-col justify-end p-4 text-white">
            <span className="inline-flex w-fit text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-primary-600 mb-2">
              Featured Recipe
            </span>
            <h1 className="text-xl font-bold drop-shadow-md">{heroRecipe.title}</h1>
            {heroRecipe.description && (
              <p className="text-sm text-white/80 mt-1 line-clamp-2">{heroRecipe.description}</p>
            )}
            <div className="flex items-center gap-3 mt-2 text-xs text-white/85">
              {heroRecipe.cuisine && <span>{heroRecipe.cuisine}</span>}
              {heroRecipe.totalTime > 0 && <span>{heroRecipe.totalTime} min</span>}
              {heroRecipe.communityRating > 0 && <span>★ {heroRecipe.communityRating}</span>}
            </div>
          </div>
        </div>
      )}

      <section>
        <SectionHeader icon={Globe} title="Explore by Region" />
        <div className="-mx-4 px-4 overflow-x-auto">
          <div className="flex gap-2 pb-1">
            {activeRegions.map((region) => (
              <button
                key={region}
                onClick={() => navigate(`/browse?region=${encodeURIComponent(region)}`)}
                className="flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium bg-white dark:bg-night-surface border border-gray-200 dark:border-night-card text-gray-700 dark:text-night-text hover:border-gray-300"
              >
                {region}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section>
        <SectionHeader icon={TrendingUp} title="Trending Recipes" onViewAll={() => navigate('/browse?sort=rating')} />
        <HorizontalScroll>
          {trendingRecipes.map((r) => <RecipeCard key={r.id} recipe={r} />)}
        </HorizontalScroll>
      </section>

      <section>
        <SectionHeader icon={Clock} title="Recently Added" onViewAll={() => navigate('/browse?sort=newest')} />
        <HorizontalScroll>
          {newRecipes.map((r) => <RecipeCard key={r.id} recipe={r} />)}
        </HorizontalScroll>
      </section>

      <section>
        <SectionHeader icon={Sparkles} title="Browse by Category" />
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.key}
              onClick={() => { setActiveCategory(cat.name); navigate('/browse'); }}
              className="flex flex-col items-center gap-1 p-3 rounded-xl bg-white dark:bg-night-surface border border-gray-100 dark:border-night-card hover:border-primary-200 dark:hover:border-night-muted hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
            >
              <span className="text-2xl" aria-hidden="true">{cat.emoji}</span>
              <span className="text-xs font-medium text-gray-700 dark:text-night-text text-center">{cat.name}</span>
            </button>
          ))}
        </div>
      </section>

      <Card>
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">{recipes.length}</div>
            <div className="text-xs text-gray-500 dark:text-night-muted uppercase tracking-wide">Recipes</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
              {new Set(recipes.map((r) => r.cuisine).filter(Boolean)).size}
            </div>
            <div className="text-xs text-gray-500 dark:text-night-muted uppercase tracking-wide">Cuisines</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
              {new Set(recipes.map((r) => r.country).filter(Boolean)).size}
            </div>
            <div className="text-xs text-gray-500 dark:text-night-muted uppercase tracking-wide">Countries</div>
          </div>
        </div>
      </Card>
    </div>
  );
}
